'use client';
import { Form, Formik } from 'formik';
import React, { useEffect, useRef, memo } from 'react';
import { Card, CardBody, Col, Container, Row } from 'reactstrap';
import { Toast } from 'react-bootstrap';
import './GCForm.css';
import { useErrorApiHandler } from '../../hooks/useErrorApiHandler';
import GeneralFormTitleShow from './generalFormTitleShow/GeneralFormTitleShow';
import useMobileSize from '../../../goldTrading/hooks/useMobileSize';

const ValidationErrorNotifier = memo(({ errors, onValidationError, submitCount }) => {
  const prevSubmitCountRef = useRef(0);
  const onValidationErrorRef = useRef(onValidationError);
  onValidationErrorRef.current = onValidationError;
  useEffect(() => {
    const hasErrors = errors && Object.keys(errors).length > 0;
    if (hasErrors && submitCount > prevSubmitCountRef.current) {
      prevSubmitCountRef.current = submitCount;
      const fn = onValidationErrorRef.current;
      if (typeof fn === 'function') fn(errors);
    }
  }, [submitCount, errors]);
  return null;
});
ValidationErrorNotifier.displayName = 'ValidationErrorNotifier';

const GeneralForm = ({
  FormFieldsComponent,
  title,
  hasTitle = true,
  initialValues,
  submitHandler,
  buttonText,
  buttonLoadingText,
  buttonLoading,
  validationSchema,
  hasCancelButton,
  hasSubmitButton = true,
  cancelText,
  onCancel,
  enableReinitialize,
  isSubmitting,
  hasAuthenticationButton = true,
  hasResetForm = false,
  hasNoBorder = false,
  shouldBeEnd = false,
  shouldUpdateLicense = false,
  setShouldUpdateLicense = false,
  formId,
  onValidationError,
}) => {
  const isMobile = useMobileSize();
  const { serverErrors, clearErrors, clearFieldError, handleError, hasErrors, getFieldError, toastMessage, showToastState, handleToastClose } = useErrorApiHandler();
  const onSubmitHandler = (values, { setSubmitting, resetForm }) => {
    clearErrors();
    hasResetForm && resetForm();
    const submitData = {
      ...values,
    };

    submitHandler(submitData, { setSubmitting, resetForm });
  };

  const handleCancelClick = () => {
    if (onCancel) {
      onCancel();
    }
  };

  return (
    <div className={hasAuthenticationButton ? 'authentication-box' : ''}>
      <Row className=' align-items-center justify-content-center g-0'>
        <Col xl='12' lg='12' md='12' sm='12' xs='12' className='form-login order-lg-1 order-2 d-flex align-items-center'>
          <Card style={{ width: isMobile ? '100vw' : '100%', border: hasNoBorder && 'none' }}>
            <CardBody className='card-body p-4 p-md-5'>
              {hasTitle && <GeneralFormTitleShow title={title} />}
              <Formik
                initialValues={initialValues}
                enableReinitialize={true}
                validationSchema={validationSchema}
                onSubmit={onSubmitHandler}
              >
                {({ errors, touched, submitCount }) => {
                  const allErrors = {
                    ...errors,
                    ...serverErrors,
                  };
                  return (
                    <>
                      {onValidationError && submitCount > 0 && Object.keys(errors).length > 0 && (
                        <ValidationErrorNotifier errors={errors} onValidationError={onValidationError} submitCount={submitCount} />
                      )}
                      <Form id={formId}>
                      {FormFieldsComponent && (
                        <FormFieldsComponent
                          errors={allErrors}
                          touched={touched}
                          serverErrors={serverErrors}
                          clearFieldError={clearFieldError}
                          setShouldUpdateLicense={setShouldUpdateLicense && setShouldUpdateLicense}
                          shouldUpdateLicense={shouldUpdateLicense && shouldUpdateLicense}
                        />
                      )}
                      {hasSubmitButton && !formId && (
                        <div className='d-grid gap-2 mt-4' style={{ display: 'flex', flexDirection: 'row', justifyContent: shouldBeEnd ? 'left' : 'space-between', gap: '10px' }}>
                          <button type='submit' className='confirm-button' disabled={isSubmitting} style={isSubmitting ? { opacity: 0.7, cursor: 'not-allowed' } : {}}>
                            {isSubmitting ? <span className='loading-spinner'></span> : <span className='confirm-text'>{buttonText}</span>}
                          </button>

                          {hasCancelButton && (
                            <button type='button' className='cancel-button' onClick={handleCancelClick}>
                              <span className='confirm-text'>{cancelText}</span>
                            </button>
                          )}
                        </div>
                      )}
                      </Form>
                    </>
                  );
                }}
              </Formik>
            </CardBody>
          </Card>
        </Col>
      </Row>

      <Toast className={'rtl position-fixed toast-error color-white z-index-top'} show={showToastState} onClose={handleToastClose} autohide={false}>
        <Toast.Body>{toastMessage}</Toast.Body>
      </Toast>
    </div>
  );
};

export default GeneralForm;
